import React, { useState, useEffect, useRef, useMemo } from 'react';
import { string, bool } from 'prop-types';
import clsx from 'clsx';
import { shallow } from 'zustand/shallow';
import ChatManager from 'socket/ChatManager';
import { useChatStore } from 'stores/chat';
import useChatTextStore from 'stores/chat-text';
import bingoAssetsStore from 'stores/bingoAssetsStore';
import { useUpdateEffect } from 'react-use';
import { text } from 'polyglot/polyglot';

import useUserStore from 'stores/user';

import Avatar from 'components/Chat/Avatar/Avatar';
import Scrollbars from 'components/Scrollbars/Scrollbars';

import PinIcon from 'themes/common/svg/pin-icon.svg';

import InputField from './InputField/InputField';

import styles from './MessageList.module.scss';

const userSelector = (state) => state.user;
const chatroomsSelector = (state) => state.chatrooms;
const messageTextSelector = (state) => [state.isWritten, state.setIsWritten];
const isClosedSelector = (state) => state.groupChatIsClosed;
const userIsBlockedSelector = (state) => state.userIsBlocked;
const lastUpdatedSelector = (state) => state.lastUpdated;
const assetsSelector = (state) => state.assets;
const fetchAssetSelector = (state) => state.fetchAsset;

function MessageList({ currentRoomId, isPrivate }) {
  const groupChatIsClosed = useChatTextStore(isClosedSelector);
  const chatUser = useUserStore(userSelector);
  const chatrooms = useChatStore(chatroomsSelector);
  const currentRoomIndex = chatrooms.findIndex(
    (room) => room.roomId === currentRoomId
  );
  const [chatIsClosed, setChatIsClosed] = useState(false);
  const userIsBlocked = useChatTextStore(userIsBlockedSelector);
  const lastUpdated = useChatStore(lastUpdatedSelector);
  const assets = bingoAssetsStore(assetsSelector);
  const fetchAsset = bingoAssetsStore(fetchAssetSelector);

  const [isAdminChat, setIsAdminChat] = useState(
    chatrooms[currentRoomIndex]?.isAdminChat
  );

  const inputRef = useRef();
  const listRef = useRef();
  const scrollRef = useRef();

  const [messages, setMessages] = useState(
    chatrooms[currentRoomIndex]?.messages
  );
  const [messageText, setMessageText] = useChatTextStore(
    messageTextSelector,
    shallow
  );

  const [pinnedMessages, setPinnedMessages] = useState([]);

  useEffect(() => {
    if (currentRoomIndex >= 0 && chatrooms[currentRoomIndex]) {
      setPinnedMessages(chatrooms[currentRoomIndex].pinnedMessages || []);
      if (chatrooms[currentRoomIndex].isPrivate) {
        setChatIsClosed(chatrooms[currentRoomIndex].chatParticipantCount <= 1);
      }
    }
  }, [chatrooms, currentRoomIndex, lastUpdated]);

  useEffect(() => {
    if (messages && messages.length > 0) {
      const chatHosts = messages.filter(
        (msg) => msg.isChatHost && msg.userName
      );
      chatHosts.forEach((chatHost) => {
        if (chatHost && chatHost.userName && !assets[chatHost.userName]) {
          fetchAsset(chatHost.userName, chatHost.chatHostImage);
        }
      });
    }
  }, [messages, fetchAsset, assets]);

  useEffect(() => {
    setMessages(chatrooms[currentRoomIndex].messages);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastUpdated]);

  const onChange = (e) => {
    if (messageText.length >= 250) return;
    setMessageText(e.target.value);
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!messageText) return;

    ChatManager.instance.sendMessage(messageText, currentRoomId, isPrivate);
    setMessageText('');
    inputRef.current.focus();
  };

  useEffect(() => {
    if (listRef.current && scrollRef.current && messages.length > 0) {
      scrollRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages.length]);

  // Update message history, when change of room.
  useUpdateEffect(() => {
    if (currentRoomIndex < 0 || !chatrooms[currentRoomIndex]) return;
    const newRoomIndex = chatrooms.findIndex(
      (room) => room.roomId === currentRoomId
    );
    setMessages(chatrooms[newRoomIndex].messages);
    setMessageText('');
    setIsAdminChat(chatrooms[newRoomIndex].isAdminChat);
    if (groupChatIsClosed && !chatrooms[newRoomIndex].isPrivate) {
      setChatIsClosed(true);
    } else if (
      chatrooms[newRoomIndex].isPrivate &&
      chatrooms[newRoomIndex].chatParticipantCount < 1
    ) {
      setChatIsClosed(true);
    } else {
      setChatIsClosed(false);
    }
  }, [currentRoomId]);

  useEffect(() => {
    if (!chatrooms[currentRoomIndex].isPrivate) {
      if (groupChatIsClosed) {
        setMessageText('');
        setChatIsClosed(true);
      } else {
        setChatIsClosed(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupChatIsClosed]);

  const filteredMessages = useMemo(() => {
    if (!messages || !pinnedMessages?.length) return messages;

    const pinnedIds = new Set(pinnedMessages.map((msg) => msg.chatMessageId));

    return messages.filter((msg) => !pinnedIds.has(msg.chatMessageId));
  }, [messages, pinnedMessages]);

  const sysMsgMap = {
    Left_Chat: 'participantLeftMessage',
    Joined_Chat: 'participantJoinedMessage',
  };

  return (
    <div className={styles.wrapper}>
      {pinnedMessages.length > 0 && (
        <div className={styles.pinnedMessagesContainer}>
          {pinnedMessages.map(
            ({
              chatMessageId,
              message,
              userName,
              nickname,
              isChatHost,
              isBlackListed,
            }) => (
              <div
                key={`pinned-${chatMessageId}`}
                className={clsx(styles.pinnedMessage, {
                  [styles.isAdmin]: isChatHost,
                })}
              >
                <div className={clsx(styles.pinnedIndicator)}>
                  <PinIcon className={styles.pinIcon} />
                </div>
                <p className={styles.pinnedText}>
                  {!isBlackListed && <b>{`${userName || nickname}: `}</b>}
                  {message}
                </p>
              </div>
            )
          )}
        </div>
      )}
      <Scrollbars className={styles.scroll} ref={scrollRef}>
        <div className={styles.list} ref={listRef}>
          {filteredMessages.map(
            ({
              sent,
              message,
              userName,
              nickname,
              avatarId,
              isChatHost,
              id,
              isBlackListed,
              type,
            }) => {
              const isMine =
                chatUser.nickname === userName ||
                chatUser.nickname === nickname;
              return (
                <div
                  key={sent || id}
                  className={clsx(styles.message, {
                    [styles.isBlacklistedMessage]: isBlackListed,
                    [styles.isAdmin]: isChatHost,
                    [styles.isMine]: isMine,
                    [styles.isPrivate]: isPrivate && isMine,
                    [styles.isPrivateAdminChat]:
                      isAdminChat && isPrivate && isMine,
                    [styles.isSysMsg]: type === 1,
                  })}
                >
                  {isChatHost && assets[userName] ? (
                    <div
                      className={clsx(styles.chathostImgWrapper, styles.avatar)}
                    >
                      <img src={assets[userName].imageData} alt="" />
                    </div>
                  ) : (
                    <Avatar avatar={avatarId} className={styles.avatar} />
                  )}

                  {type !== 1 ? (
                    <p className={styles.text}>
                      {!isBlackListed && <b>{`${userName || nickname}: `}</b>}
                      {message}
                    </p>
                  ) : (
                    <p className={styles.sysText}>
                      {text.t(`${'chat.'}${sysMsgMap[message]}`, {
                        name: userName,
                      })}
                    </p>
                  )}
                </div>
              );
            }
          )}
        </div>
      </Scrollbars>

      <InputField
        key={chatIsClosed}
        ref={inputRef}
        value={messageText}
        isDisabled={!chatUser.isAuthenticated || userIsBlocked}
        anonymousUserFunc={
          chatUser.callback_login &&
          typeof chatUser.callback_login === 'function' &&
          !chatUser.isAuthenticated
            ? chatUser.callback_login
            : null
        }
        onChange={onChange}
        onSubmit={onSubmit}
        chatIsClosed={chatIsClosed}
      />
    </div>
  );
}

MessageList.propTypes = {
  currentRoomId: string.isRequired,
  isPrivate: bool.isRequired,
};

export default MessageList;
